import type { Work } from '@/mocks/movies';
import { formatViews } from '@/utils/content';

interface DetailHeroProps {
  work: Work;
  isFavorite: boolean;
  onToggleFavorite: () => void;
  onShare: () => void;
}

export default function DetailHero({ work, isFavorite, onToggleFavorite, onShare }: DetailHeroProps) {
  const infoRows = [
    { label: '导演', value: work.director },
    { label: '类型', value: work.genres.join(' / ') },
    { label: '年份', value: String(work.year) },
    { label: '地区', value: work.region },
    { label: '状态', value: work.status },
  ];

  return (
    <section className="relative w-full overflow-hidden bg-primary-950">
      <img src={work.backdrop} alt={`${work.title} 主视觉`} className="absolute inset-0 h-full w-full object-top opacity-40" />
      <div className="absolute inset-0 bg-gradient-to-t from-primary-950 via-primary-950/85 to-primary-950/60" />

      <div className="relative mx-auto flex w-full flex-col gap-6 px-4 py-10 md:flex-row md:gap-8 md:px-6 md:py-14">
        <div className="w-40 shrink-0 self-center overflow-hidden rounded-lg border border-primary-800 bg-primary-900 md:w-56 md:self-start">
          <div className="relative w-full pt-[140%]">
            <img
              src={work.cover}
              alt={`${work.title} 海报`}
              title={work.title}
              className="absolute inset-0 h-full w-full object-top"
            />
          </div>
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-md bg-accent-500 px-2.5 py-0.5 text-xs font-bold text-primary-950">
              {work.typeLabel}
            </span>
            {work.topics.length > 0 && (
              <span className="rounded-md border border-background-300/40 px-2.5 py-0.5 text-xs text-background-200">
                专题推荐
              </span>
            )}
          </div>

          <h1 className="mt-3 font-heading text-2xl font-black text-background-50 md:text-4xl">{work.title}</h1>
          <p className="mt-2 text-sm uppercase tracking-[0.18em] text-background-300">{work.subtitle}</p>

          <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-background-200">
            <span className="flex items-center gap-1 font-bold text-accent-400">
              <i className="ri-star-fill" aria-hidden="true" />
              {work.rating.toFixed(1)} 分
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-eye-line" aria-hidden="true" />
              {formatViews(work.views)} 次浏览
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-calendar-line" aria-hidden="true" />
              {work.year}
            </span>
          </div>

          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-background-200">{work.synopsis}</p>

          <dl className="mt-6 grid grid-cols-1 gap-x-8 gap-y-2.5 sm:grid-cols-2">
            {infoRows.map((row) => (
              <div key={row.label} className="flex items-baseline gap-3 border-b border-primary-800/50 pb-2">
                <dt className="w-12 shrink-0 text-xs text-background-400">{row.label}</dt>
                <dd className="text-sm text-background-100">{row.value}</dd>
              </div>
            ))}
            <div className="flex items-baseline gap-3 border-b border-primary-800/50 pb-2 sm:col-span-2">
              <dt className="w-12 shrink-0 text-xs text-background-400">主演</dt>
              <dd className="text-sm text-background-100">{work.cast.join('、')}</dd>
            </div>
          </dl>

          <div className="mt-7 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={onToggleFavorite}
              className={`inline-flex items-center gap-2 whitespace-nowrap rounded-md px-5 py-2.5 text-sm font-semibold transition-colors ${
                isFavorite
                  ? 'bg-accent-500 text-primary-950 hover:bg-accent-400'
                  : 'border border-background-300/50 text-background-100 hover:border-accent-400 hover:text-accent-400'
              }`}
            >
              <i className={isFavorite ? 'ri-heart-fill text-lg' : 'ri-heart-line text-lg'} aria-hidden="true" />
              {isFavorite ? '已收藏' : '收藏本片'}
            </button>
            <button
              type="button"
              onClick={onShare}
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300/50 px-5 py-2.5 text-sm font-medium text-background-100 transition-colors hover:border-accent-400 hover:text-accent-400"
            >
              <i className="ri-share-line text-lg" aria-hidden="true" />
              分享
            </button>
            <span className="text-xs text-background-400">收藏信息仅保存在本机浏览器，不涉及登录</span>
          </div>
        </div>
      </div>
    </section>
  );
}